<template>
	<div
		:class="[
			'inline-editing',
			showHyperlink && 'inline-editing--hyperlink',
			disabled && 'is-disabled',
			errors.length && 'has-error'
		]"
	>
		<Label
			class="inline-editing__label"
			:labelText="labelText"
			v-if="labelText"
		/>
		<div class="inline-editing__body">
			<ReadonlyField
				v-if="showHyperlink"
				class="inline-editing__readonly"
				data-test="readonlyField"
			>
				<a class="link" :href="entryData">
					{{ linkText }}
				</a>
			</ReadonlyField>
			<div v-else class="inline-editing__input-container">
				<Input
					class="inline-editing__input"
					:disabled="!editMode"
					v-model="entryData"
					data-test="inputText"
					:placeholder="placeholder"
					:style="inlineStyle"
				/>
				<i class="icon icon-error-solid" />
			</div>

			<div class="inline-editing__btn-container">
				<Button
					class="inline-editing__btn-add"
					className="btn--icon-only-circle"
					iconClassName="icon-more"
					:iconBeforeLabel="true"
				/>
				<ActionableButton
					class="inline-editing__action-btn"
					:apiCallFn="apiCallFn"
					placement="right"
					:hasPendingChanges="hasPendingChanges"
					:successMessage="successMessage"
					:errorMessage="errorMessage"
					data-test="actionable-button"
					@save="handlerSave"
					@edit="handlerEdit"
					@close="handlerClose"
				/>
				<Button
					class="inline-editing__btn-deleted"
					className="btn--icon-only-circle"
					iconClassName="icon-delete-glenn"
					:iconBeforeLabel="true"
				/>
			</div>
		</div>
		<HelpFeedback
			class="inline-editing__help-feedback"
			:helpText="helpText"
			:style="inlineStyle"
			v-if="helpText"
		/>
		<ErrorFeedback
			class="inline-editing__error-feedback"
			:errorMessages="errors"
			v-if="errors.length > 0"
			:style="inlineStyle"
		/>
	</div>
</template>

<script>
import ReadonlyField from '@/components/forms/readonly-field/readonly-field';
import Input from '@/components/forms/input/input';
import ActionableButton from '@/components/forms/actionable/actionable-button';
import ErrorFeedback from '@/components/forms/error-feedback/error-feedback';
import HelpFeedback from '@/components/forms/help-feedback/help-feedback';
import formComponentMixin from '@/mixins/form-component-mixin';
import Label from '@/components/forms/label/label';
import Button from '@/components/forms/button/button';

export default {
	name: 'InlineEditing',
	mixins: [formComponentMixin],
	components: {
		ActionableButton,
		ReadonlyField,
		ErrorFeedback,
		HelpFeedback,
		Label,
		Button,
		Input
	},
	props: {
		value: {
			type: String,
			default: ''
		},
		apiCallFn: {
			type: Function,
			default: () => {},
			require: true
		},
		successMessage: {
			type: String,
			default: 'Success!'
		},
		errorMessage: {
			type: String,
			default: 'Fail!'
		},
		timeout: {
			type: Number,
			default: 3000
		},
		helpText: {
			type: String,
			default: null
		},
		labelText: {
			type: String,
			default: null
		},
		linkText: {
			type: String
		},
		width: {
			type: [String, Number],
			default: null
		},
		placeholder: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			editMode: false,
			entryData: this.value
		};
	},
	computed: {
		showHyperlink() {
			return !this.editMode && this.variant == 'hyperlink';
		},
		hasPendingChanges() {
			return this.value !== this.entryData;
		},
		inlineStyle() {
			if (typeof this.width === 'number') {
				return {
					width: `${this.width}px`
				};
			} else {
				return {
					width: this.width
				};
			}
		}
	},
	methods: {
		handlerEdit(value) {
			this.editMode = value;
		},
		handlerSave(obj) {
			this.editMode = !obj.success;
			if (obj.success) {
				this.$emit('save', this.entryData);
			}
		},
		handlerClose(val) {
			if (val) {
				this.entryData = this.value;
			}
		}
	},
	watch: {
		value(newValue) {
			this.entryData = newValue;
		},
		entryData(val) {
			this.$emit('change', val);
		}
	}
};
</script>

<style lang="scss" scoped>
@import '@/scss/5-objects/_form.scss';
.inline-editing {
	display: inline-grid;
	grid-template-columns: auto 1fr;
	align-items: center;
	text-align: left;
	font-size: $input-font-size;
	&__readonly {
		padding: $padding-sm 0;
	}

	&__label {
		grid-column: 1/2;
		margin-right: $gutter-md;
		margin-bottom: 0;
	}

	&__btn-container {
		display: inline-flex;
		align-items: center;
		gap: $gutter-md;
	}

	&__help-feedback,
	&__error-feedback {
		grid-column: 2/3;
		word-wrap: break-word;
	}

	&__error-feedback {
		grid-row: 3/4;
	}

	&__body {
		display: inline-flex;
		align-items: center;
		gap: $gutter-md;
	}

	&__input-container {
		position: relative;
		display: flex;
		@include textfield;
		width: 100%;

		&:focus-within {
			-o-outline: $focus-outline;
			-moz-outline: $focus-outline;
			outline: $focus-outline;
			outline-offset: $focus-offset;
		}
	}

	&__input {
		display: inline-block;
		appearance: none;
		outline: none;
		border: none;
		@include input-height;
		cursor: pointer;
		font-size: $input-font-size;
		text-overflow: ellipsis;
		padding: $padding-sm;
		width: 100%;
		background-color: $white;
		border-radius: $border-radius;

		& ~ .icon {
			position: relative;
			display: inline-block;
			@include horizontal-center-with-transform;
			right: 0;
			padding: $input-padding-y $input-padding-x;
			font-size: $default-font-size;
			border-left: $form-input-border;
			cursor: pointer;
		}

		& ~ .icon-error-solid {
			display: none;
			border: none;
			color: $ui-negative;
		}
	}

	&.has-error {
		.inline-editing {
			&__input-container {
				-o-outline: $focus-outline;
				-moz-outline: $focus-outline;
				outline: $focus-outline;
				outline-offset: $focus-offset;
				outline-color: $ui-negative;
			}

			&__input {
				padding-right: 28px;
			}
		}

		.icon-error-solid {
			display: inline-block;
		}
	}

	&::v-deep .btn--icon-only-circle {
		font-size: 18px;
	}

	&::v-deep .readonly-field {
		grid-template-columns: auto 1fr;
		column-gap: 0;
		&:not(:last-child) {
			margin-bottom: 0;
		}
	}
}
</style>
